import React, { useEffect, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment";

import { Layout } from "components/Layout";
import { Loader } from "components/Loader";
import { Input } from "components/forms/Input";
import { finishLoading, showAlert, startLoading } from "redux/actions/setAppActions";
import { GET_PUPIL } from "redux/types";

/**
 * @api {get} /pupils/:id/edit страница редактирования ученика
 * @apiGroup pupils
 * @apiName страница редактирования ученика
 *
 * @apiParam {String} id ID ученика
 *
 * @apiDescription <p>Страница редактирования данных ученика</p>
 * <p>Компонент <code>EditPupilPage.js</code> <a href="../client/src/pages/pupils/EditPupilPage.js">ссылка на файл</a></p>
 */

export const EditPupilPage = () => {
    const pupilId = useParams().id;
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation(["pupils", "registration", "translation"]);
    const { loading } = useSelector((state) => state.app);

    const [form, setForm] = useState({
        sex: "1",
        surname: "",
        name: "",
        patronymic: "",
        birthday: "",
    });
    const [incorrectFields, setIncorrectFields] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Получаем данные ученика
    useEffect(() => {
        const fetchPupilData = async () => {
            try {
                setIsLoading(true);

                // Всегда загружаем актуальные данные с сервера
                const { data } = await axios.get(`pupil/${pupilId}`);
                setForm({
                    sex: data.sex || "1",
                    surname: data.surname || "",
                    name: data.name || "",
                    patronymic: data.patronymic || "",
                    birthday: data.birthday ? moment(data.birthday).format("DD.MM.YYYY") : "",
                });
            } catch (err) {
                const text = err.response?.data?.message || t("pupil_load_error", { ns: "translation" });
                dispatch(showAlert({ type: "error", text }));
            } finally {
                setIsLoading(false);
            }
        };

        fetchPupilData();
    }, [pupilId, dispatch, t]);

    const changeHandler = (e) => {
        const { name, value } = e.target;

        if (incorrectFields.includes(name)) {
            setIncorrectFields(incorrectFields.filter((item) => item !== name));
        }

        if (["name", "surname", "patronymic"].includes(name) && value.length > 70) {
            return;
        }

        // Не обрезаем пробелы, чтобы разрешить двойные имена/фамилии/отчества
        setForm({
            ...form,
            [name]: value,
        });
    };

    const validateForm = () => {
        const errorFields = [];
        const forbiddenSymbols = /[%;="*?#&()_~]/gi;

        // Обрезаем пробелы только для валидации
        const trimmedSurname = form.surname.trim();
        const trimmedName = form.name.trim();

        if (trimmedSurname.length < 2 || trimmedSurname.length > 70 || forbiddenSymbols.test(trimmedSurname)) {
            errorFields.push("surname");
        }
        if (trimmedName.length < 2 || trimmedName.length > 70 || forbiddenSymbols.test(trimmedName)) {
            errorFields.push("name");
        }
        if (!moment(form.birthday, "DD.MM.YYYY", true).isBetween(-1577934000000, moment.now())) {
            errorFields.push("birthday");
        }

        return errorFields;
    };

    const handleSave = async () => {
        const errorFields = validateForm();

        if (errorFields.length > 0) {
            setIncorrectFields(errorFields);
            const formFields = {
                surname: t("surname", { ns: "registration" }),
                name: t("name", { ns: "registration" }),
                birthday: t("birthday", { ns: "registration" }),
            };
            const incorrectFieldsMessage = errorFields.map((item) => formFields[item]).join("\n");
            dispatch(
                showAlert({
                    type: "error",
                    text: `${t("incorrectly_filled_fields", { ns: "translation" })}:\n\n${incorrectFieldsMessage}`,
                }),
            );
            return;
        }

        try {
            dispatch(startLoading());

            // Обрезаем пробелы перед отправкой на сервер
            const data = {
                sex: form.sex,
                surname: form.surname.trim(),
                name: form.name.trim(),
                patronymic: form.patronymic.trim(),
                birthday: form.birthday,
            };

            const ISODate = moment(data.birthday, "DD.MM.YYYY", true).toISOString();

            if (ISODate) {
                data.birthday = ISODate;
            }

            const response = await axios.put(`pupil/${pupilId}/update-info`, data);

            // Обновляем данные ученика в Redux store
            dispatch({ type: GET_PUPIL, payload: response.data });

            dispatch(finishLoading());
            dispatch(showAlert({ type: "success", text: t("pupil_updated_success") }));

            // Возвращаемся на страницу профиля ученика
            history.push(`/pupils/${pupilId}`);
        } catch (err) {
            const text = err.response?.data?.message || t("pupil_update_error");
            dispatch(finishLoading());
            dispatch(showAlert({ type: "error", text }));
        }
    };

    const handleBack = () => {
        history.push(`/pupils/${pupilId}`);
    };

    if (isLoading || loading) {
        return <Loader />;
    }

    return (
        <Layout>
            <div className="page page__small auth-page">
                <header className="page__header">
                    <button
                        onClick={handleBack}
                        className="icon-btn page__icon-btn page__icon-btn_left icon-btn_back"
                    />
                    <p className="page__title">{t("edit_pupil_data")}</p>
                </header>

                <div className="auth-page__wrapper">
                    <div className="auth-page__input-group auth-page__input-group_row">
                        <div className="input-field">
                            <input
                                id="m"
                                type="radio"
                                name="sex"
                                value="1"
                                checked={form.sex === "1"}
                                onChange={changeHandler}
                            />
                            <label htmlFor="m">{t("male", { ns: "registration" })}</label>
                        </div>
                        <div className="input-field">
                            <input
                                id="f"
                                type="radio"
                                name="sex"
                                value="2"
                                checked={form.sex === "2"}
                                onChange={changeHandler}
                            />
                            <label htmlFor="f">{t("female", { ns: "registration" })}</label>
                        </div>
                    </div>

                    <div className="auth-page__input-group">
                        <Input
                            id="surname"
                            type="text"
                            name="surname"
                            placeholder={t("surname", { ns: "registration" })}
                            onChange={changeHandler}
                            value={form.surname}
                            isError={incorrectFields.includes("surname")}
                        />
                        <Input
                            id="name"
                            type="text"
                            name="name"
                            onChange={changeHandler}
                            placeholder={t("name", { ns: "registration" })}
                            value={form.name}
                            isError={incorrectFields.includes("name")}
                        />
                        <Input
                            id="patronymic"
                            type="text"
                            name="patronymic"
                            onChange={changeHandler}
                            placeholder={t("patronymic", { ns: "registration" })}
                            value={form.patronymic}
                            isError={incorrectFields.includes("patronymic")}
                        />
                        <Input
                            id="birthday"
                            type="text"
                            name="birthday"
                            placeholder={t("birthday", { ns: "registration" })}
                            onChange={changeHandler}
                            value={form.birthday}
                            isError={incorrectFields.includes("birthday")}
                            mask={Date}
                            maskOptions={{
                                min: new Date(1950, 0, 1),
                                max: new Date(),
                                unmask: true,
                            }}
                        />
                    </div>

                    <div className="auth-page__buttons" style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
                        <button onClick={handleSave} className="main-btn auth-page__btn" style={{ flex: 1 }}>
                            {t("save", { ns: "translation" })}
                        </button>
                        <button
                            onClick={handleBack}
                            className="main-btn auth-page__btn"
                            style={{ flex: 1, background: "#6c757d" }}
                        >
                            {t("back", { ns: "translation" })}
                        </button>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
